import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Alert } from '../components/ui/Alert';
import { Sheet } from '../components/ui/Sheet';
import { toast } from 'sonner';
import { apiFetch, parseJsonResponse, isNetworkErrorResponse, getImageUrl } from '../utils/api';

interface Icon {
  id: number;
  tipo: 'aguacate' | 'picante' | 'recomendado';
  name: string;
  image_path: string;
  image_url?: string;
  is_active: number;
  is_default: number;
  created_at: string;
  updated_at: string;
}

export const Icons: React.FC = () => {
  const { token } = useAuth();
  const [iconos, setIconos] = useState<Icon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingIcon, setEditingIcon] = useState<Icon | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [selectedTipo, setSelectedTipo] = useState<string>('');
  const [formData, setFormData] = useState({
    tipo: 'aguacate' as 'aguacate' | 'picante' | 'recomendado',
    name: '',
    image_path: '',
    is_active: 1,
    is_default: 0
  });

  // Cargar iconos
  useEffect(() => {
    if (token) {
      loadIcons();
    }
  }, [token, selectedTipo]);

  // Función helper para formatear el tamaño del archivo
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const loadIcons = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://hiomenu.local/api';
      const url = selectedTipo 
        ? `${API_BASE_URL}.php?route=icons/list&tipo=${selectedTipo}`
        : `${API_BASE_URL}.php?route=icons/list`;
      
      const res = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al cargar iconos');
        return;
      }
      
      const json = await parseJsonResponse(res);
      if (json.data?.iconos) {
        setIconos(json.data.iconos);
      }
    } catch (error) {
      console.error('Error cargando iconos:', error);
      toast.error('Error al cargar iconos');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 2 * 1024 * 1024; // 2MB
    
    setFileSize(file.size);
    
    if (file.size > maxSize) {
      toast.warning(`El archivo es de ${formatFileSize(file.size)}, excede el límite de 2MB. La subida podría fallar.`);
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    const allowedTypes = ['png', 'svg', 'jpg', 'jpeg'];
    if (!extension || !allowedTypes.includes(extension)) {
      toast.error('Tipo de archivo no permitido. Formatos permitidos: PNG, SVG, JPG');
      setFileSize(null);
      return;
    }

    setImageFile(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile || !token) return null;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('icon_image', imageFile);
      
      const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://hiomenu.local/api';
      const res = await fetch(`${API_BASE_URL}.php?route=icons/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!res.ok) {
        const json = await res.json();
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al subir imagen';
        throw new Error(errorMessage);
      }

      const json = await res.json();
      return json.data?.image_path || json.image_path || null;
    } catch (err: any) {
      console.error('Error completo:', err);
      toast.error(err.message || 'Error al subir imagen');
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!token) return;

    try {
      let imagePath = formData.image_path;

      if (imageFile) {
        const uploadedPath = await uploadImage();
        if (!uploadedPath) {
          toast.error('Error al subir la imagen');
          return;
        }
        imagePath = uploadedPath;
      }

      if (!imagePath) {
        toast.error('Debes subir una imagen');
        return;
      }

      const payload: any = {
        ...formData,
        image_path: imagePath,
        is_active: formData.is_active ? 1 : 0,
        is_default: formData.is_default ? 1 : 0
      };

      const route = editingIcon ? 'icons/update' : 'icons/create';
      
      if (editingIcon) {
        payload.id = editingIcon.id;
      }

      const res = await apiFetch(
        route,
        {
          method: 'POST',
          body: JSON.stringify(payload)
        },
        token
      );

      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión');
        return;
      }

      const json = await parseJsonResponse(res);

      if (json.error || !json.success) {
        let errorMessage = editingIcon ? 'Error al actualizar icono' : 'Error al crear icono';
        if (json.error) {
          errorMessage = typeof json.error === 'string' ? json.error : String(json.error);
        } else if (json.message) {
          errorMessage = typeof json.message === 'string' ? json.message : String(json.message);
        }
        toast.error(errorMessage);
      } else {
        toast.success(editingIcon ? 'Icono actualizado exitosamente' : 'Icono creado exitosamente');
        resetForm();
        loadIcons();
      }
    } catch (error: any) {
      console.error('Error al guardar icono:', error);
      toast.error(error.message || 'Error al guardar icono');
    }
  };

  const handleEdit = (icon: Icon) => {
    setEditingIcon(icon);
    setFormData({
      tipo: icon.tipo,
      name: icon.name,
      image_path: icon.image_path,
      is_active: icon.is_active,
      is_default: icon.is_default
    });
    setImagePreview(icon.image_url || getImageUrl(icon.image_path) || null);
    setImageFile(null);
    setFileSize(null);
    setShowForm(true);
  };

  const handleDelete = async (iconId: number) => {
    if (!token) return;
    if (!window.confirm('¿Estás seguro de que deseas eliminar este icono?')) return;

    try {
      const res = await apiFetch(
        'icons/delete',
        {
          method: 'DELETE',
          body: JSON.stringify({ id: iconId })
        },
        token
      );

      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión');
        return;
      }

      const json = await parseJsonResponse(res);

      if (json.error || !json.success) {
        let errorMessage = 'Error al eliminar icono';
        if (json.error) {
          errorMessage = typeof json.error === 'string' ? json.error : String(json.error);
        } else if (json.message) {
          errorMessage = typeof json.message === 'string' ? json.message : String(json.message);
        }
        toast.error(errorMessage);
      } else {
        toast.success('Icono eliminado exitosamente');
        loadIcons();
      }
    } catch (error: any) {
      console.error('Error al eliminar icono:', error);
      toast.error('Error al eliminar icono');
    }
  };

  const resetForm = () => {
    setFormData({
      tipo: 'aguacate',
      name: '',
      image_path: '',
      is_active: 1,
      is_default: 0
    });
    setImageFile(null);
    setImagePreview(null);
    setFileSize(null);
    setEditingIcon(null);
    setShowForm(false);
  };

  const handleCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const handleFormCancel = () => {
    resetForm();
  };

  const getTipoLabel = (tipo: string) => {
    const labels: Record<string, string> = {
      aguacate: '🥑 Aguacate',
      picante: '🌶️ Picante',
      recomendado: '⭐ Recomendado'
    };
    return labels[tipo] || tipo;
  };

  const getTipoColor = (tipo: string) => {
    const colors: Record<string, string> = {
      aguacate: 'from-green-600 to-green-700',
      picante: 'from-red-600 to-red-700',
      recomendado: 'from-yellow-600 to-yellow-700'
    };
    return colors[tipo] || 'from-gray-600 to-gray-700';
  };

  // Agrupar iconos por tipo
  const iconosPorTipo = iconos.reduce((acc, icon) => {
    if (!acc[icon.tipo]) {
      acc[icon.tipo] = [];
    }
    acc[icon.tipo].push(icon);
    return acc;
  }, {} as Record<string, Icon[]>);

  return (
    <DashboardLayout
      title="Gestión de Iconos Globales"
      subtitle="Administra los iconos para productos (aguacate, picante, recomendado)"
      showBackButton
      backPath="/management"
    >
      {/* Sheet para formulario */}
      <Sheet
        isOpen={showForm}
        onClose={handleFormCancel}
        title={editingIcon ? 'Editar Icono' : 'Nuevo Icono'}
        subtitle={editingIcon ? 'Modifica la información del icono' : 'Completa los datos para crear un nuevo icono'}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Tipo de Icono *
            </label>
            <select
              value={formData.tipo}
              onChange={(e) => setFormData({ ...formData, tipo: e.target.value as 'aguacate' | 'picante' | 'recomendado' })}
              className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              disabled={!!editingIcon}
            >
              <option value="aguacate">🥑 Aguacate</option>
              <option value="picante">🌶️ Picante</option>
              <option value="recomendado">⭐ Recomendado</option>
            </select>
          </div>

          <Input
            label="Nombre del Icono"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ej: Icono de Aguacate Premium"
            required
          />

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Imagen del Icono *
            </label>
            <input
              type="file"
              accept=".png,.svg,.jpg,.jpeg"
              onChange={handleImageChange}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-lg file:border-0
                file:text-sm file:font-semibold
                file:bg-red-50 file:text-red-700
                hover:file:bg-red-100
                file:cursor-pointer"
            />
            <p className="mt-1 text-xs text-gray-500">
              Formatos permitidos: PNG, SVG, JPG. Tamaño máximo: 2MB
            </p>
            {imagePreview && (
              <div className="mt-4">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-w-full h-auto max-h-48 rounded-lg border border-gray-300"
                />
                {fileSize !== null && (
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className={`font-medium ${
                      fileSize > 2 * 1024 * 1024 
                        ? 'text-orange-600' 
                        : 'text-gray-600'
                    }`}>
                      Tamaño: {formatFileSize(fileSize)}
                    </span>
                    {fileSize > 2 * 1024 * 1024 && (
                      <span className="text-orange-600 font-semibold">
                        ⚠️ Excede el límite
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Estado
              </label>
              <select
                value={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value, 10) })}
                className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value={1}>Activo</option>
                <option value={0}>Inactivo</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Predeterminado
              </label>
              <select
                value={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: parseInt(e.target.value, 10) })}
                className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value={0}>No</option>
                <option value={1}>Sí</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Solo un icono puede ser predeterminado por tipo
              </p>
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="primary"
              isLoading={uploading}
              disabled={uploading}
              onClick={(e) => handleSubmit(e)}
              className="flex-1"
            >
              {editingIcon ? 'Actualizar Icono' : 'Crear Icono'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleFormCancel}
              className="flex-1"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Sheet>

      <div className="space-y-6">
        {/* Filtro por tipo y botón agregar */}
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              <div className="flex-1">
                <Select
                  label="Filtrar por Tipo"
                  value={selectedTipo}
                  onChange={(e) => setSelectedTipo(e.target.value)}
                  options={[
                    { value: '', label: '-- Todos los tipos --' },
                    { value: 'aguacate', label: '🥑 Aguacate' },
                    { value: 'picante', label: '🌶️ Picante' },
                    { value: 'recomendado', label: '⭐ Recomendado' }
                  ]}
                />
              </div>
              <Button
                variant="primary"
                size="md"
                onClick={handleCreate}
                className="whitespace-nowrap"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Agregar Icono
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <Alert variant="error">{error}</Alert>
        )}

        {/* Lista de Iconos */}
        {loading ? (
          <Card>
            <CardContent>
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-4"></div>
                <p className="text-gray-600">Cargando iconos...</p>
              </div>
            </CardContent>
          </Card>
        ) : iconos.length === 0 ? (
          <Card>
            <CardContent>
              <div className="p-12 text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <p className="font-medium mb-2">No hay iconos configurados</p>
                <p className="text-sm">
                  Haz clic en "Agregar Icono" para crear uno nuevo
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(iconosPorTipo).map(([tipo, iconosTipo]) => (
              <div key={tipo}>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {getTipoLabel(tipo)}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {iconosTipo.map((icon) => (
                    <Card key={icon.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 mb-1 truncate">
                              {icon.name}
                            </h4>
                          </div>
                          <div className="flex flex-col gap-1 ml-2">
                            {icon.is_default === 1 && (
                              <Badge variant="warning" className="text-xs">
                                Predeterminado
                              </Badge>
                            )}
                            {icon.is_active === 1 ? (
                              <Badge variant="success" className="text-xs">
                                Activo
                              </Badge>
                            ) : (
                              <Badge variant="neutral" className="text-xs">
                                Inactivo
                              </Badge>
                            )}
                          </div>
                        </div>

                        {icon.image_url || icon.image_path ? (
                          <div className="mb-3">
                            <img
                              src={icon.image_url || getImageUrl(icon.image_path) || ''}
                              alt={icon.name}
                              className="w-full h-32 object-contain rounded-lg border border-gray-300 bg-gray-50 p-2"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23ddd"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999"%3EImagen no disponible%3C/text%3E%3C/svg%3E';
                              }}
                            />
                          </div>
                        ) : null}

                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleEdit(icon)}
                            className="flex-1"
                          >
                            Editar
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleDelete(icon.id)}
                            className="flex-1"
                          >
                            Eliminar
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

